import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Application, ApplicationSegment, FsDistributor } from '@shared/models/data-model';
import { LoadingScreenService } from '@shared/services/loading-screen.service';
import { collectFormErrors } from '@shared/formHelpers';
import { AmplitudeService } from '@shared/services/amplitude.service';
import { DTRUM_ACTION_NAME, DynatraceHelperService } from '@shared/services/dynatraceHelper.service';
import { RegistrationService } from '@shared/services/registration.service';
import { REGISTRATION_STEP_ID } from '@shared/services/steps.service';
import { APP_CONFIG, AppConfig } from '@src/app/app-config.module';
import { NGXLogger } from 'ngx-logger';

@Component({
    selector: 'terms-condition',
    templateUrl: './terms-condition.component.html',
    styleUrls: ['./terms-condition.component.scss']
})
export class TermsConditionComponent implements OnInit, OnDestroy {
    private readonly stepId = REGISTRATION_STEP_ID.TERMS;
    private application: Application;
    form: FormGroup<{
        financialTerms: FormControl<boolean>;
        discountCode: FormControl<string | null>;
        cloudTerms?: FormControl<boolean>;
        startupConfirm?: FormControl<boolean>;
        earlyAccessConfirm?: FormControl<boolean>;
    }>;
    loading = true;
    termsLink: string;
    showCode: boolean = false;
    isEligibleForStartupOffer: boolean;
    isDirectSales: boolean;
    showSubmitFallback = false;
    isEpaCandidate: boolean;

    constructor(
        protected logger: NGXLogger,
        @Inject(APP_CONFIG) private readonly config: AppConfig,
        private readonly fb: FormBuilder,
        private readonly registrationService: RegistrationService,
        private readonly amplitudeService: AmplitudeService,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly dynatrace: DynatraceHelperService,
        private readonly loadingScreenService: LoadingScreenService
    ) { }

    ngOnInit() {
        this.application = this.route.snapshot.data.application;

        if (this.application.distributor === FsDistributor.Italian) {
            this.autoCompleteApplication();
            return;
        }

        this.isDirectSales = this.application.segment === ApplicationSegment.direct;
        this.isEligibleForStartupOffer = this.registrationService.isEligibleForStartupOffer();
        this.isEpaCandidate = this.route.snapshot.data.isEpa.earlyAccessPhaseCandidate;
        const productMarket = this.registrationService.getProductMarket();
        this.form = this.fb.group({
            financialTerms: [false, Validators.requiredTrue],
            discountCode: [this.application.discountCode ?? null, Validators.maxLength(50)]
        });
        if (!this.isDirectSales) {
            this.form.addControl("cloudTerms", this.fb.control(false, Validators.requiredTrue));
        }

        if (this.isEligibleForStartupOffer) {
            this.form.addControl("startupConfirm", this.fb.control(false, Validators.requiredTrue));
        }

        if (this.isEpaCandidate) {
            this.form.addControl("earlyAccessConfirm", this.fb.control(false, Validators.requiredTrue));
        }

        this.showCode = Boolean(this.application.discountCode);

        this.termsLink = this.config.termsAndConditionsLink[productMarket] || this.config.termsAndConditionsLink.fallback;
        this.loading = false;
    }

    ngOnDestroy() {
        this.registrationService.activateProgressBar();
    }

    // Applications distributed by the Italian entity have already signed terms offline:
    // silently replay the same completion the checkbox form would trigger, never showing it.
    private autoCompleteApplication() {
        this.showSubmitFallback = false;
        this.registrationService.deactivateProgressBar();
        this.loadingScreenService.show('LOADING_SCREEN.FINALIZING_APPLICATION');
        this.completeApplicationAndAdvance(this.application.discountCode)
            .catch((err) => {
                this.logger.error("Auto-completion failed for Italian distributor application: ", err);
                this.showSubmitFallback = true;
            })
            .finally(() => {
                this.loadingScreenService.hide();
            });
    }

    retrySubmitFallback() {
        this.autoCompleteApplication();
    }

    onSubmit(): boolean {
        this.form.markAllAsTouched();
        if (this.form.valid) {
            this.loading = true;
            this.completeApplicationAndAdvance(this.form.value.discountCode ?? undefined).catch(() => { });
            return true;
        } else {
            this.loading = false;
            this.amplitudeService.trackFormSubmissionRejected(this.route.snapshot.data['pageName'], collectFormErrors(this.form));
            this.logger.error("form not valid: ", this.form.value);
            return false;
        }
    }

    private completeApplicationAndAdvance(discountCode?: string): Promise<void> {
        const completeActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.COMPLETE_APPLICATION);

        return this.registrationService.completeApplication(discountCode).then(() => {
            this.registrationService.completeStep(this.stepId);
            return this.router.navigate([this.registrationService.getNextStep()]).then(() => {
                this.dynatrace.leaveAction(completeActionId);
            });
        }, (err) => {
            this.logger.trace("FATAL: ", err);
            this.dynatrace.leaveAction(completeActionId);
            throw err;
        });
    }

    onShowCode() {
        this.showCode = true;
    }

    goToPrevious() {
        const backActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.BACK_TO_PREVIOUS);
        this.router.navigate([this.registrationService.getPreviousStep()]).then(() => {
            this.dynatrace.leaveAction(backActionId);
        });
    }
}
